import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Skeleton from "../../../components/ui/Skeleton";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { DatePicker, TimePicker } from "../../../components/ui/DateTimePickers";
import { Plus, Trash2 } from "lucide-react";
import { getSocieties, getTowersBySociety } from "../../../services/societyService";
import { getFlatsBySociety } from "../../../services/flatsService";
import { createPreApprovedInvite } from "../../../services/inviteService";
import { getStoredAuthUser, getCurrentRoleKey } from "../../../services/authService";
import { getMyProfile } from "../../../services/userService";
import { createInviteSchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import styles from "./CreateInvite.module.css";

const INVITE_TYPES = [
  { id: "guest", label: "Guest" },
  { id: "cab", label: "Cab" },
  { id: "delivery", label: "Delivery" },
];

const SUB_TYPES_BY_TYPE = {
  guest: [
    { id: "quick", label: "Quick" },
    { id: "group", label: "Party / Group" },
    { id: "frequent", label: "Frequent" },
    { id: "private", label: "Private" },
  ],
  cab: [
    { id: "once", label: "Once" },
    { id: "frequent", label: "Frequent" },
  ],
  delivery: [
    { id: "once", label: "Once" },
    { id: "frequent", label: "Frequent" },
  ],
};

const CreateInvite = () => {
  const navigate = useNavigate();
  const authUser = getStoredAuthUser();
  const currentRoleKey = getCurrentRoleKey();
  const isResidentUser = ["user", "resident", "tenant", "family-member", "flat-owner", "owner"].includes(currentRoleKey);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [societies, setSocieties] = useState([]);
  const [towers, setTowers] = useState([]);
  const [flats, setFlats] = useState([]);
  const [allowedFlatIds, setAllowedFlatIds] = useState(() => new Set());

  const [societyId, setSocietyId] = useState("");
  const [towerId, setTowerId] = useState("");
  const [unitId, setUnitId] = useState("");

  const [form, setForm] = useState({
    invite_type: "guest",
    invite_sub_type: "quick",
    title: "",
    visitor_name: "",
    mobile_number: "",
    company_name: "",
    vehicle_number: "",
    purpose: "",
    valid_from: "",
    valid_to: "",
    start_time: "",
    end_time: "",
    entries_per_day: 1,
    max_guest_count: 10,
    allowed_days: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
    is_private: false,
    approval_required: false,
    guests: [],
  });

  const hasAssignedUnits = !isResidentUser || allowedFlatIds.size > 0;

  const flatsForTower = useMemo(() => {
    const nextTowerId = Number(towerId);
    if (!nextTowerId) return [];
    return (flats || [])
      .filter((f) => Number(f.tower_id) === nextTowerId)
      .filter((f) => (allowedFlatIds.size ? allowedFlatIds.has(Number(f.flat_id)) : true));
  }, [allowedFlatIds, flats, towerId]);

  const allowedTowers = useMemo(() => {
    if (isResidentUser && !allowedFlatIds.size) return [];
    if (!allowedFlatIds.size) return towers || [];
    const towerIds = new Set(
      (flats || [])
        .filter((f) => allowedFlatIds.has(Number(f.flat_id)))
        .map((f) => Number(f.tower_id))
        .filter(Boolean)
    );
    return (towers || []).filter((t) => towerIds.has(Number(t.tower_id)));
  }, [allowedFlatIds, flats, isResidentUser, towers]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const societyData = await getSocieties();
      const active = societyData || [];
      setSocieties(active);

      let nextSocietyId = Number(active?.[0]?.society_id) || "";
      let nextAllowedFlatIds = new Set();

      if (isResidentUser) {
        nextSocietyId = Number(authUser?.society_id) || nextSocietyId;
        try {
          const profile = await getMyProfile();
          nextAllowedFlatIds = new Set(
            (profile?.flat_mappings || [])
              .map((m) => Number(m?.flat_id))
              .filter((n) => Number.isFinite(n) && n > 0)
          );
          setAllowedFlatIds(nextAllowedFlatIds);
        } catch (e) {
          console.error("LOAD PROFILE FOR INVITE ERROR:", e);
          setAllowedFlatIds(new Set());
          nextAllowedFlatIds = new Set();
        }
      } else {
        setAllowedFlatIds(new Set());
      }

      setSocietyId(nextSocietyId ? String(nextSocietyId) : "");

      if (nextSocietyId) {
        const [towerData, flatData] = await Promise.all([
          getTowersBySociety(nextSocietyId),
          getFlatsBySociety(nextSocietyId),
        ]);
        const nextTowers = towerData || [];
        const nextFlats = flatData || [];
        setTowers(nextTowers);
        setFlats(nextFlats);

        if (isResidentUser && nextAllowedFlatIds.size) {
          const towerIds = new Set(
            nextFlats
              .filter((f) => nextAllowedFlatIds.has(Number(f.flat_id)))
              .map((f) => Number(f.tower_id))
              .filter(Boolean)
          );
          const first = nextTowers.find((t) => towerIds.has(Number(t.tower_id))) || nextTowers[0];
          if (first) setTowerId(String(first.tower_id));
        } else if (isResidentUser && !nextAllowedFlatIds.size) {
          setTowerId("");
          setUnitId("");
        } else if (nextTowers.length) {
          setTowerId(String(nextTowers[0].tower_id));
        }
      }
    } catch (error) {
      console.error("LOAD CREATE INVITE ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to load data");
    } finally {
      setLoading(false);
    }
  }, [authUser?.society_id, isResidentUser]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const run = async () => {
      const nextSocietyId = Number(societyId);
      if (!nextSocietyId) return;
      try {
        const [towerData, flatData] = await Promise.all([
          getTowersBySociety(nextSocietyId),
          getFlatsBySociety(nextSocietyId),
        ]);
        const nextTowers = towerData || [];
        const nextFlats = flatData || [];
        setTowers(nextTowers);
        setFlats(nextFlats);

        if (isResidentUser && allowedFlatIds.size) {
          const towerIds = new Set(
            nextFlats
              .filter((f) => allowedFlatIds.has(Number(f.flat_id)))
              .map((f) => Number(f.tower_id))
              .filter(Boolean)
          );
          const first = nextTowers.find((t) => towerIds.has(Number(t.tower_id))) || nextTowers[0];
          if (first) setTowerId(String(first.tower_id));
        } else if (isResidentUser && !allowedFlatIds.size) {
          setTowerId("");
          setUnitId("");
        } else if (nextTowers.length) {
          setTowerId(String(nextTowers[0].tower_id));
        }
      } catch (error) {
        console.error("RELOAD SOCIETY DATA ERROR:", error);
      }
    };

    run();
  }, [allowedFlatIds, isResidentUser, societyId]);

  useEffect(() => {
    setUnitId("");
  }, [towerId]);

  useEffect(() => {
    const nextType = form.invite_type;
    const allowed = SUB_TYPES_BY_TYPE[nextType] || [];
    if (!allowed.find((s) => s.id === form.invite_sub_type)) {
      setForm((prev) => ({
        ...prev,
        invite_sub_type: allowed?.[0]?.id || "quick",
      }));
    }
  }, [form.invite_type, form.invite_sub_type]);

  const updateForm = (key, value) =>
    setForm((prev) => {
      if (key === "mobile_number") {
        const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
        return { ...prev, mobile_number: digits };
      }
      if (key === "entries_per_day") {
        const digits = String(value || "").replace(/\D/g, "");
        const num = Math.max(1, Math.min(99, Number(digits || 1)));
        return { ...prev, entries_per_day: num };
      }
      if (key === "max_guest_count") {
        const digits = String(value || "").replace(/\D/g, "");
        const num = Math.max(1, Math.min(500, Number(digits || 10)));
        return { ...prev, max_guest_count: num };
      }
      
      const nextState = { ...prev, [key]: value };
      
      if (key === "valid_from" && nextState.valid_to && nextState.valid_to < value) {
        nextState.valid_to = value;
      }
      if (key === "valid_to" && nextState.valid_from && value < nextState.valid_from) {
        nextState.valid_to = nextState.valid_from;
      }

      return nextState;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextUnitId = Number(unitId);
    if (isResidentUser && allowedFlatIds.size && !allowedFlatIds.has(nextUnitId)) {
      return toast.error("Select your own unit");
    }

    const parsed = createInviteSchema.safeParse({
      ...form,
      society_id: societyId,
      tower_id: towerId,
      unit_id: unitId,
    });
    if (!parsed.success) return toast.error(zodErrorToFirstMessage(parsed.error));

    setSubmitting(true);
    try {
      const res = await createPreApprovedInvite(parsed.data);
      toast.success("Invite created successfully");
      navigate(`/resident/invites/${res?.invite?.id || ""}`);
    } catch (error) {
      console.error("CREATE INVITE ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to create invite");
    } finally {
      setSubmitting(false);
    }
  };

  const subTypes = SUB_TYPES_BY_TYPE[form.invite_type] || [];
  const isGroup = form.invite_sub_type === "group";
  const showVisitorName = form.invite_type === "guest" || form.invite_type === "cab";
  
  const addGuest = () =>
    updateForm("guests", [
      ...(form.guests || []),
      { guest_name: "", mobile_number: "", vehicle_number: "" },
    ]);

  const updateGuest = (index, key, value) =>
    updateForm(
      "guests",
      (form.guests || []).map((g, i) => {
        if (i !== index) return g;
        if (key === "mobile_number") {
          return { ...g, mobile_number: String(value || "").replace(/\D/g, "").slice(0, 10) };
        }
        return { ...g, [key]: value };
      })
    );

  const removeGuest = (index) =>
    updateForm(
      "guests",
      (form.guests || []).filter((_, i) => i !== index)
    );

  if (loading) {
    return (
      <AdminLayout title="Create Invite">
        <div style={{ padding: "24px" }}>
          <Skeleton variant="card" count={3} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Create Invite">
      <form onSubmit={handleSubmit} className={styles.pageGrid}>
        <div className={styles.card}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 700, margin: "0 0 4px 0" }}>Create Pre-Approved Invite</h2>
            <p style={{ fontSize: "13px", color: "var(--color-text-muted)", margin: 0 }}>
              Issue digital gate pass for expected visitors, cabs, or parcel deliveries.
            </p>
          </div>

          <div className={styles.fieldsGrid}>
            <FormField label="Society" required>
              <SelectDropdown
                value={societyId}
                onChange={(e) => setSocietyId(e.target.value)}
                disabled={isResidentUser}
                options={societies.map((s) => ({
                  label: s.society_name,
                  value: String(s.society_id),
                }))}
              />
            </FormField>

            <FormField label="Tower / Wing" required>
              <SelectDropdown
                value={towerId}
                onChange={(e) => setTowerId(e.target.value)}
                disabled={!hasAssignedUnits}
                options={(allowedTowers || []).map((t) => ({
                  label: t.tower_name,
                  value: String(t.tower_id),
                }))}
              />
            </FormField>

            <FormField label="Unit / Flat" required>
              <SelectDropdown
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!hasAssignedUnits}
                options={[
                  { label: hasAssignedUnits ? "Select unit" : "No units assigned", value: "" },
                  ...flatsForTower.map((f) => ({
                    label: f.flat_number,
                    value: String(f.flat_id),
                  })),
                ]}
              />
            </FormField>

            <FormField label="Invite Type" required>
              <SelectDropdown
                value={form.invite_type}
                onChange={(e) => updateForm("invite_type", e.target.value)}
                options={INVITE_TYPES.map((t) => ({ label: t.label, value: t.id }))}
              />
            </FormField>

            <FormField label="Sub Type" required>
              <SelectDropdown
                value={form.invite_sub_type}
                onChange={(e) => updateForm("invite_sub_type", e.target.value)}
                options={subTypes.map((t) => ({ label: t.label, value: t.id }))}
              />
            </FormField>

            <FormField label="Title" required helperText="e.g. Mom visiting / Amazon delivery">
              <TextInput
                value={form.title}
                onChange={(e) => updateForm("title", e.target.value)}
                placeholder="Brief label for pass"
              />
            </FormField>

            {showVisitorName ? (
              <FormField label="Visitor Name">
                <TextInput
                  value={form.visitor_name}
                  onChange={(e) => updateForm("visitor_name", e.target.value)}
                  placeholder="Guest name"
                />
              </FormField>
            ) : null}

            <FormField label="Mobile Number">
              <TextInput
                value={form.mobile_number}
                onChange={(e) => updateForm("mobile_number", e.target.value)}
                placeholder="10-digit mobile"
                type="tel"
                prefixText="+91"
                maxLength={10}
              />
            </FormField>

            <FormField label="Company Name" helperText="Cab / Delivery service provider">
              <TextInput
                value={form.company_name}
                onChange={(e) => updateForm("company_name", e.target.value)}
                placeholder="e.g. Uber / Swiggy / BlueDart"
              />
            </FormField>

            <FormField label="Vehicle Number">
              <TextInput
                value={form.vehicle_number}
                onChange={(e) => updateForm("vehicle_number", e.target.value)}
                placeholder="e.g. MH 02 AB 1234"
              />
            </FormField>

            <div className={styles.fieldSpan3}>
              <FormField label="Purpose / Notes">
                <TextInput
                  value={form.purpose}
                  onChange={(e) => updateForm("purpose", e.target.value)}
                  placeholder="Reason for visit"
                />
              </FormField>
            </div>

            <FormField label="Valid From">
              <DatePicker
                value={form.valid_from}
                onChange={(e) => updateForm("valid_from", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </FormField>

            <FormField label="Valid To">
              <DatePicker
                value={form.valid_to}
                onChange={(e) => updateForm("valid_to", e.target.value)}
                min={form.valid_from || new Date().toISOString().split("T")[0]}
              />
            </FormField>

            <FormField label="Time Window (Start & End)">
              <div className={styles.timeGrid}>
                <TimePicker
                  value={form.start_time}
                  onChange={(e) => updateForm("start_time", e.target.value)}
                />
                <TimePicker
                  value={form.end_time}
                  onChange={(e) => updateForm("end_time", e.target.value)}
                />
              </div>
            </FormField>
          </div>

          {isGroup ? (
            <div className={styles.groupCard}>
              <div className={styles.groupTitle}>Group Guests (optional)</div>
              <div className={styles.groupHint}>
                Add guest list to generate individual pass codes for party/gathering.
              </div>

              <div className={styles.guestList}>
                {(form.guests || []).map((g, idx) => (
                  <div key={idx} className={styles.guestRow}>
                    <TextInput
                      placeholder="Guest name"
                      value={g.guest_name || ""}
                      onChange={(e) => updateGuest(idx, "guest_name", e.target.value)}
                    />
                    <TextInput
                      placeholder="Mobile"
                      value={g.mobile_number || ""}
                      onChange={(e) => updateGuest(idx, "mobile_number", e.target.value)}
                      type="tel"
                      prefixText="+91"
                      maxLength={10}
                    />
                    <TextInput
                      placeholder="Vehicle (optional)"
                      value={g.vehicle_number || ""}
                      onChange={(e) => updateGuest(idx, "vehicle_number", e.target.value)}
                    />
                    <div className={styles.guestActions}>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => removeGuest(idx)}
                      >
                        <Trash2 size={14} style={{ marginRight: "4px" }} /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={addGuest}>
                    <Plus size={14} style={{ marginRight: "4px" }} /> Add Guest
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div className={styles.footer} style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate("/resident/invites")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              Generate Pass
            </Button>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default CreateInvite;
